import React, { useEffect, useState } from "react";
import ReactModal from "react-modal";
import style from "./SendMerchantLink.module.scss";
import { GetEmailData, SendLink, GetData, sendApplicationLinkToMerchant } from "../../../../../api";
import ButtonIcon from "../../../../components/ButtonIcon/ButtonIcon";
import CloseIcon16 from "../../../../assets/svg/icons/common/CloseIcon16";
import ReCAPTCHA from "react-google-recaptcha";
import config from "../../../../../config";
import toast from "react-hot-toast";
import useCommonStore from "../../../../stores/common";
import useApplicationStore from "../../../../stores/application";
import useDomainStore from "../../../../stores/domain";

function SendMerchantLink({ open, onClose }) {
  const activeApp = useApplicationStore((state) => state.activeApp);
  const { isKurvDomain } = useDomainStore();
  const isV2Enabled = activeApp?.app_version == 2;
  const [modalLoader, setModalLoader] = useState(false);
  const [note, setNote] = useState("");
  const [emailSent, setEmailSent] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [clickable, setClickable] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [showDropDown, setShowDropDown] = useState(true);
  const [emailAddress, setEmailAddress] = useState("");
  const [emails, setEmails] = useState([]);
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const GOOGLE_CAPTCHA = config.paymentcloud.GOOGLE_CAPTCHA;

  const handleEmailChange = (e) => {
    const value = e.target.value;
    if (value === "Other") {
      setShowOther(true);
      setEmailAddress(""); // Reset email address when "Other" is selected
    } else {
      setShowOther(false);
      setEmailAddress(value);
    }
  };

  const handleNoteChange = (e) => {
    setNote(e.target.value);
  };

  const handleCaptchaChange = (value) => {
    setCaptchaToken(value);
    setCaptchaError("");
  };

  const handleSend = async () => {
    setModalLoader(true);
    setEmailText("");
    setCaptchaError("");
    const toastId = toast.loading('Sending...', { duration: 15 * 1000 });
    try {
      let response;
      
      if (isV2Enabled) {
        // Use V2 method
        response = await sendApplicationLinkToMerchant(activeApp.editslug, {
          email: emailAddress || undefined,
        });
      } else {
        // Use legacy method
        response = await SendLink({
          slug: activeApp.editslug,
          email: emailAddress,
          note: note,
          captchaToken: captchaToken,
        });
      }

      if (response.status) {
        toast.success("Application link sent to merchant", { id: toastId, duration: 5 * 1000 })
        onClose();
        setEmailSent(true);
        setClickable(false);
        setEmailText("E-mail Sent Successfully.");

        setTimeout(() => {
          setEmailSent(false);
          setEmailText("");
          setClickable(true);
          setCaptchaToken("");
          toggleSendLinkModal(false);
        }, 3000);
      } else {
        setEmailText("Something went wrong. Try again later.");
        toast.error("Something went wrong. Try again later.", { id: toastId, duration: 5 * 1000 })
        setTimeout(() => {
          setEmailText("");
          setCaptchaToken("");
        }, 3000);
      }
    } catch (error) {
      const errorResponse = error.request?.response ? JSON.parse(error.request.response) : {};
      toast.error(errorResponse.message || "Something went wrong. Try again later.", { id: toastId, duration: 5 * 1000 })
      if (errorResponse.errors) {
        if (errorResponse.errors.email) {
          setEmailText(errorResponse.errors.email[0]);
        } else if (!isV2Enabled && errorResponse.errors.captchaToken) {
          setCaptchaError("Please fill the captcha");
        } else {
          setEmailText(errorResponse.message || "Something went wrong. Try again later.");
        }
      } else {
        if (!isV2Enabled && errorResponse.captchaMsg) {
          setCaptchaError(errorResponse.captchaMsg);
        }
        setEmailText(errorResponse.message || "Something went wrong. Try again later.");
      }
    } finally {
      setModalLoader(false);
    }
  };

  useEffect(() => {
    setModalLoader(true);
    GetEmailData(activeApp.editslug)
      .then((email_data_response) => {
        let dba_name = email_data_response.data?.dba_name || "Merchant";
        let agent_name = email_data_response.data?.agent_name || "Agent Name";
        let organisation_name =
          email_data_response.data?.organisation_name || "Organization Name";

        setNote(
          `Dear ${dba_name}, ${agent_name} from ${organisation_name} has started a merchant services application on your behalf and has sent you the link below to review and complete. Please click and fill in the remaining fields to finish up your portion of the application process!`,
        );
      })
      .catch(() => {
        setEmailText("Something went wrong. Try again later.");
      })
      .finally(() => {
        setModalLoader(false);
      }); // Ensure loader is stopped after request

    // get data from activeApp
    const email_list = [activeApp?.field_businessemail, activeApp?.field_email].filter(Boolean);
    setShowDropDown(email_list.length > 0);
    setShowOther(email_list.length === 0);
    setEmails([...email_list, "Other"]);

  }, [activeApp.editslug]); // Add dependency so it runs when `activeApp.editslug` changes

  return (
    <ReactModal isOpen={open} className={style.react_modal}>
      <div className={style.header}>
        <ButtonIcon onClick={onClose}>
          <CloseIcon16 />
        </ButtonIcon>
      </div>

      <div className={style.body}>
        <h6>Send link to Merchant</h6>

        <div className={style.form_group}>
          <label htmlFor="email">Email</label>
          <select
            className={style.input_select}
            name="email"
            id="email"
            value={showOther ? "Other" : emailAddress}
            onChange={handleEmailChange}
          >
            <option value="" disabled>
              Select Email
            </option>
            {emails?.map((email, index) => (
              <option key={index} value={email}>
                {email}
              </option>
            ))}
          </select>
        </div>

        {/* Custom Email Input (if "Other" is selected) */}
        {showOther && (
          <div className={style.form_group}>
            <label htmlFor="custom-email">Enter Email</label>
            <input
              type="email"
              id="custom-email"
              className={style.input_select}
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="Enter email address"
            />
          </div>
        )}

        {!isV2Enabled && (
          <div className={style.form_group}>
            <label htmlFor="comment">Comment</label>
            <textarea
              className={style.input_select}
              name="comment"
              id="comment"
              rows="5"
              value={note}
              onChange={handleNoteChange}
              onClick={() => setClickable(true)}
              onBlur={() => setClickable(false)}
              placeholder="Enter your note"
            ></textarea>
            {clickable ? (
              <small className="text-warning">* Please edit the text.</small>
            ) : (
              <small className="text-info">
                * Click here to edit the text.
              </small>
            )}
          </div>
        )}

        {/* Captcha */}
        {!isV2Enabled && (
          <div className={style.form_group}>
            {GOOGLE_CAPTCHA && (
              <ReCAPTCHA
                sitekey={GOOGLE_CAPTCHA}
                onChange={handleCaptchaChange}
                size="normal"
              />
            )}
            {captchaError && (
              <small className="text-danger">* {captchaError}</small>
            )}
          </div>
        )}
      </div>

      <div className={style.footer_modal}>
        <div className={style.button_group}>
          <ButtonIcon textColor="#254FE2" onClick={onClose}>
            Cancel
          </ButtonIcon>
          {!emailSent && (
            <ButtonIcon
              bgColor={isKurvDomain ? "#22A276" : "#4C70EF"}
              textColor="#FFF"
              onClick={handleSend}
            >
              Send
            </ButtonIcon>
          )}
        </div>
      </div>
    </ReactModal>
  );
}

export default SendMerchantLink;
